import { useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Share2, Droplets, Sun, Sparkles, Shield, Zap } from "lucide-react";

const quizQuestions = [
  {
    id: 1,
    question: "¿Cómo sientes tu piel 30 minutos después de lavarla?",
    options: [
      { label: "Suave y cómoda", value: "A" },
      { label: "Tirante y seca", value: "B" },
      { label: "Muy brillante/grasa", value: "C" },
      { label: "Grasa en la zona T, seca en mejillas", value: "D" },
      { label: "Roja o irritada", value: "E" }
    ]
  },
  {
    id: 2,
    question: "¿Cómo se ven tus poros frente al espejo?",
    options: [
      { label: "Casi invisibles", value: "A" },
      { label: "Muy pequeños, casi cerrados", value: "B" },
      { label: "Grandes y visibles en todo el rostro", value: "C" },
      { label: "Visibles solo en nariz y frente", value: "D" },
      { label: "Normales, pero con rojeces", value: "E" }
    ]
  },
  {
    id: 3,
    question: "¿Cómo reacciona tu piel a productos nuevos?",
    options: [
      { label: "Rara vez tengo problemas", value: "A" },
      { label: "A veces siento ardor leve", value: "B" },
      { label: "Me salen granitos fácilmente", value: "C" },
      { label: "Depende de la zona", value: "D" },
      { label: "Casi siempre me irrito o arde", value: "E" }
    ]
  },
  {
    id: 4,
    question: "¿Con qué frecuencia tienes brotes o acné?",
    options: [
      { label: "Casi nunca", value: "A" },
      { label: "Nunca", value: "B" },
      { label: "Muy frecuentemente", value: "C" },
      { label: "Solo en la zona T (frente/nariz)", value: "D" },
      { label: "A veces, acompañados de irritación", value: "E" }
    ]
  },
  {
    id: 5,
    question: "¿Cómo describirías la textura de tu piel?",
    options: [
      { label: "Uniforme y suave", value: "A" },
      { label: "Áspera o descamada", value: "B" },
      { label: "Gruesa y aceitosa", value: "C" },
      { label: "Desigual dependiendo la zona", value: "D" },
      { label: "Fina y delicada", value: "E" }
    ]
  }
];

const results = {
  A: { 
    title: "Piel Normal", 
    desc: "Tu piel está equilibrada. No es demasiado grasa ni demasiado seca. Tienes poros finos, buena circulación sanguínea y una textura aterciopelada, suave y lisa.", 
    color: "bg-emerald-50 text-emerald-900 border-emerald-200",
    icon: Sun
  },
  B: { 
    title: "Piel Seca", 
    desc: "Tu piel produce menos sebo que la piel normal. Como resultado, carece de los lípidos que necesita para retener la humedad y formar un escudo protector frente a influencias externas.", 
    color: "bg-sky-50 text-sky-900 border-sky-200",
    icon: Droplets
  },
  C: { 
    title: "Piel Grasa", 
    desc: "Tu piel tiene una producción acrecentada de sebo. La hiperproducción se conoce como seborrea. Tienes poros grandes y visibles y brillo constante.", 
    color: "bg-amber-50 text-amber-900 border-amber-200",
    icon: Sparkles
  },
  D: { 
    title: "Piel Mixta", 
    desc: "Tu piel es una mezcla de tipos. Tienes zona T (frente, barbilla y nariz) grasa, poros agrandados en esta zona y tal vez con algunas impurezas, y mejillas normales a secas.", 
    color: "bg-violet-50 text-violet-900 border-violet-200",
    icon: Shield
  },
  E: { 
    title: "Piel Sensible", 
    desc: "Tu piel se irrita fácilmente y es más reactiva que la piel normal. Reacciona a estímulos externos a los que otras pieles no reaccionan (viento, sol, productos).", 
    color: "bg-rose-50 text-rose-900 border-rose-200",
    icon: Zap
  }
};

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const LandingScreen = ({ onStart }) => (
  <motion.div
    variants={pageVariants}
    initial="initial"
    animate="animate"
    exit="exit"
    transition={{ duration: 0.4, ease: "easeOut" }}
    className="min-h-screen flex items-center justify-center p-6"
    data-testid="landing-screen"
  >
    <div className="quiz-card max-w-xl w-full text-center">
      <span className="caption-text" data-testid="caption-text">DIAGNÓSTICO DERMATOLÓGICO</span>
      <h1 className="heading-primary mt-4 mb-6" data-testid="main-heading">
        Identifica tu Tipo de Piel en 2 Minutos
      </h1>
      <p className="body-text mb-10" data-testid="description-text">
        Responde 5 preguntas rápidas pensando en cómo siente tu piel la mayor parte del tiempo y descubre qué tipo de piel tienes.
      </p>
      <button
        onClick={onStart}
        className="btn-primary inline-flex items-center gap-2"
        data-testid="start-quiz-btn"
      >
        Comenzar Test
        <ArrowRight size={20} strokeWidth={1.5} />
      </button>
    </div>
  </motion.div>
);

const QuestionScreen = ({ question, currentIndex, totalQuestions, onAnswer, selectedAnswer }) => {
  const progress = ((currentIndex) / totalQuestions) * 100;

  return (
    <motion.div
      key={question.id}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="min-h-screen flex items-center justify-center p-6"
      data-testid={`question-screen-${question.id}`}
    >
      <div className="quiz-card max-w-xl w-full">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="caption-text" data-testid="question-counter">
              PREGUNTA {currentIndex + 1} DE {totalQuestions}
            </span>
            <span className="text-sm text-slate-500">{Math.round(progress)}%</span>
          </div>
          <div 
            className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="progress-bar"
          >
            <div 
              className="h-full bg-slate-900 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <h2 className="heading-secondary mb-8" data-testid="question-text">
          {question.question}
        </h2>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => onAnswer(option.value)}
              className={`option-btn ${selectedAnswer === option.value ? 'selected' : ''}`}
              data-state={selectedAnswer === option.value ? 'selected' : 'unselected'}
              data-testid={`option-${option.value}`}
            >
              <div className="flex items-center gap-4">
                <span className="option-letter">{option.value}</span>
                <span className="text-slate-700">{option.label}</span>
              </div>
              <div className={`option-check ${selectedAnswer === option.value ? 'visible' : ''}`}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7L5.5 10.5L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ResultScreen = ({ skinType, onRestart }) => {
  const result = results[skinType];
  const Icon = result.icon;
  
  const handleShare = () => {
    const text = `Acabo de descubrir que tengo ${result.title}. ¡Haz el test y descubre tu tipo de piel!`;
    const url = window.location.href;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="min-h-screen flex items-center justify-center p-6"
      data-testid="result-screen"
    >
      <div className="quiz-card max-w-xl w-full text-center">
        <span className="caption-text" data-testid="result-caption">TU RESULTADO</span>
        
        <div className={`mt-6 mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full ${result.color}`} data-testid="result-icon">
          <Icon size={40} strokeWidth={1.5} />
        </div>
        
        <h2 className="heading-primary mb-4" data-testid="result-title">
          {result.title}
        </h2>
        
        <div className={`p-6 rounded-2xl border ${result.color} mb-8`} data-testid="result-description">
          <p className="text-base leading-relaxed">
            {result.desc}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleShare}
            className="btn-secondary inline-flex items-center justify-center gap-2"
            data-testid="share-whatsapp-btn"
          >
            <Share2 size={18} strokeWidth={1.5} />
            Compartir en WhatsApp
          </button>
          <button
            onClick={onRestart}
            className="btn-primary inline-flex items-center justify-center gap-2"
            data-testid="restart-quiz-btn"
          >
            <RotateCcw size={18} strokeWidth={1.5} />
            Repetir Test
          </button>
        </div>
      </div>
    </motion.div>
  );
};

function App() {
  const [screen, setScreen] = useState('landing'); // landing, quiz, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  const handleStart = () => {
    setScreen('quiz');
  };

  const handleAnswer = (answer) => {
    setSelectedAnswer(answer);
    
    // Delay to show selection before moving to next
    setTimeout(() => {
      const newAnswers = [...answers, answer];
      setAnswers(newAnswers);
      setSelectedAnswer(null);

      if (currentQuestion < quizQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Calculate result
        setScreen('result');
      }
    }, 300);
  };

  const calculateResult = () => {
    const counts = { A: 0, B: 0, C: 0, D: 0, E: 0 };
    answers.forEach(answer => {
      counts[answer]++;
    });
    
    let maxCount = 0;
    let result = 'A';
    Object.entries(counts).forEach(([key, count]) => {
      if (count > maxCount) {
        maxCount = count;
        result = key;
      }
    });
    
    return result;
  };

  const handleRestart = () => {
    setScreen('landing');
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedAnswer(null);
  };

  return (
    <div className="App bg-slate-50 min-h-screen" data-testid="app-container">
      <AnimatePresence mode="wait">
        {screen === 'landing' && (
          <LandingScreen key="landing" onStart={handleStart} />
        )}
        {screen === 'quiz' && (
          <QuestionScreen
            key={`question-${currentQuestion}`}
            question={quizQuestions[currentQuestion]}
            currentIndex={currentQuestion}
            totalQuestions={quizQuestions.length}
            onAnswer={handleAnswer}
            selectedAnswer={selectedAnswer}
          />
        )}
        {screen === 'result' && (
          <ResultScreen 
            key="result" 
            skinType={calculateResult()} 
            onRestart={handleRestart} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
